import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  AMT_UI_SCALE,
  AMT_UI_STEP,
  CCY_CODES,
  D,
  MONEY_SCALE,
  RATE_UI_SCALE,
  RATE_UI_STEP,
  calcProfit,
  fmtAmt,
  type CcyCode,
  type CreateOrderInput,
  type Money,
} from '@wave/shared';
import { Modal } from '../Modal';
import { Combobox } from '../Combobox';
import { FlowSteps } from '../FlowSteps';
import { FormulaHint } from '../FormulaHint';
import { useAccounts, useCurrencies, useUsdtRates } from '../../api/hooks';
import { useCreateOrder, useRefreshRate } from '../../api/mutations';
import { useAuthStore } from '../../auth/store';
import { resolveClient, resolveExec, type ClientState, type ExecState, type FieldSrc } from '../../lib/recalc2';
import {
  bdClientTotal,
  bdExecAuto,
  bdExecutorTotal,
  bdPairAmount,
  bdProfit,
  buildProfitChecks,
  buildRateChecks,
} from '../../lib/breakdown';
import { btnPrimary, btnSecondary, inputCls } from '../../lib/styles';

type FormValues = {
  client: string;
  executor: string;
  clGiveCcy: CcyCode;
  clGiveAmt: string;
  clRate: string;
  clGetCcy: CcyCode;
  clGetAmt: string;
  // Валюта, в которой мы платим исполнителю. По умолчанию USDT — реальный
  // бизнес-флоу: мы → USDT → исполнитель → нужная клиенту валюта. Может
  // быть и национальная валюта, если оператор так настраивает сделку.
  exGetCcy: CcyCode;
  exGetAmt: string;
  exRate: string;
  // Коэффициент (%): наценка на сумму клиента ПОСЛЕ расчёта курса, транзитом
  // уходит приведшему клиента. В пересчёте give×rate=get НЕ участвует.
  coefPct: string;
  // Фикс за платёжку (USDT, транзитом): прибавляется к клиенту и исполнителю.
  fixUsdt: string;
  // Бонус исполнителю (USDT): благодарность за сделку — прибавляется ТОЛЬКО к
  // оплате исполнителю и вычитается из профита (в отличие от фикса — платит компания).
  bonusUsdt: string;
  // Курьер (USDT, целиком исполнителю). Режим — отдельный state courierMode:
  // 'extra' — клиент доплачивает; 'included' — клиент получает меньше.
  courierUsdt: string;
};

// Метаданные происхождения для 5 пересчитываемых полей формы.
type ProvKey = 'clGive' | 'clGet' | 'clRate' | 'exGet' | 'exRate';
type ProvMeta = { src: FieldSrc; seq: number };
type RateDir = 'fwd' | 'inv';

const freshProv = (): Record<ProvKey, ProvMeta> => ({
  clGive: { src: 'auto', seq: 0 },
  clGet: { src: 'auto', seq: 0 },
  clRate: { src: 'auto', seq: 0 },
  exGet: { src: 'auto', seq: 0 },
  exRate: { src: 'auto', seq: 0 },
});

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Итог коэффициента: финальная сумма клиента и дельта. null — коэффициент
 * не задан/нулевой/вне диапазона (−100; 100] или база ещё не введена.
 * Та же формула и округление, что на сервере (server-of-truth дублируем
 * только для превью).
 */
function coefResult(base: string, pct: string): { final: string; delta: string } | null {
  if (!base || !pct || !/^-?\d+(\.\d+)?$/.test(pct)) return null;
  const b = D(base);
  const p = D(pct);
  if (b.lte(0) || p.isZero() || p.lte(-100) || p.gt(100)) return null;
  const final = b.times(D(1).plus(p.dividedBy(100))).toDecimalPlaces(MONEY_SCALE);
  return { final: final.toString(), delta: final.minus(b).toString() };
}

/**
 * Безопасно строит Decimal из ввода формы: пустая строка или незавершённый ввод
 * ('', '5.', '-', '.') → Decimal(0). Без этого `D('')` кидает DecimalError и вся
 * форма падает белым экраном — например когда фикс/бонус введён РАНЬШЕ сумм
 * (fix/bonus становятся активны от одной своей суммы + курсов, не дожидаясь
 * clGiveAmt/exGetAmt). Для сырых значений формы использовать dz, не D.
 */
function dz(s: string | undefined | null) {
  if (!s) return D('0');
  try {
    return D(s);
  } catch {
    return D('0');
  }
}

// Спиннеры браузера у number-инпутов бесполезны для денежных сумм и толкаются
// с контентом — прячем.
const noSpinCls =
  '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

/** Вопросик с всплывающей подсказкой (CSS-only, показывается по hover). */
function HelpTip({ children }: { children: React.ReactNode }) {
  return (
    <span className="group relative inline-flex">
      <span className="flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border border-border-strong text-[9px] font-bold leading-none text-text2 transition-colors duration-150 group-hover:border-accent group-hover:text-accent">
        ?
      </span>
      <span className="invisible absolute left-0 top-full z-30 mt-1.5 w-80 rounded-lg border border-border bg-bg2 p-3 text-[11px] font-normal normal-case leading-relaxed tracking-normal text-text shadow-lg group-hover:visible">
        {children}
      </span>
    </span>
  );
}

/** Бейдж происхождения значения: «авто» (вычислено) ↔ «вручную» (зафиксировано). */
function ProvBadge({ meta, onToggle }: { meta: ProvMeta; onToggle: () => void }) {
  const isAuto = meta.src === 'auto';
  return (
    <button
      type="button"
      tabIndex={-1}
      onClick={onToggle}
      title={
        isAuto
          ? 'Вычисляется автоматически — клик: зафиксировать значение'
          : 'Введено вручную, не перезаписывается — клик: отпустить и пересчитать'
      }
      className={`rounded-full border px-1.5 py-px text-[9px] font-bold uppercase leading-[14px] tracking-wide transition-colors duration-150 ${
        isAuto
          ? 'border-border-strong bg-bg3 text-text2 hover:text-text'
          : 'border-accent bg-accent text-white hover:opacity-85'
      }`}
    >
      {isAuto ? 'авто' : 'вручную'}
    </button>
  );
}

/**
 * Курс с переключаемым направлением котировки. В форме (RHF) хранится
 * канонический курс («сколько get за 1 give», до 8 знаков), отображается —
 * в выбранном направлении: 'inv' показывает 1/canonical (для пар типа
 * RUB→GBP, где канонический курс — мелкая дробь 0.0097).
 */
function RateInput({
  canonical,
  dir,
  onCanonicalChange,
  className,
  placeholder,
}: {
  canonical: string;
  dir: RateDir;
  onCanonicalChange: (canonical: string) => void;
  className?: string;
  placeholder?: string;
}) {
  // draft: локальный текст пока поле в фокусе (иначе курсор прыгает от пересчёта).
  const [draft, setDraft] = useState<string | null>(null);

  const display = useMemo(() => {
    if (draft !== null) return draft;
    if (!canonical) return '';
    const c = D(canonical);
    if (c.lte(0)) return canonical;
    const shown = dir === 'inv' ? D(1).dividedBy(c) : c;
    return shown.toDecimalPlaces(RATE_UI_SCALE).toString();
  }, [draft, canonical, dir]);

  function handleChange(raw: string) {
    setDraft(raw);
    if (raw === '') {
      onCanonicalChange('');
      return;
    }
    const n = D(raw.replace(',', '.') || '0');
    if (n.lte(0)) return; // незавершённый ввод — не дёргаем пересчёт
    const next = dir === 'inv' ? D(1).dividedBy(n) : n;
    onCanonicalChange(next.toDecimalPlaces(8).toString());
  }

  return (
    <input
      type="number"
      step={RATE_UI_STEP}
      value={display}
      onChange={(e) => handleChange(e.target.value)}
      onFocus={() => setDraft(display)}
      onBlur={() => setDraft(null)}
      className={className}
      placeholder={placeholder}
    />
  );
}

export function NewOrderModal() {
  const navigate = useNavigate();
  const { data: accounts } = useAccounts('all');
  const { data: rates } = useUsdtRates();
  // Валюты из справочника (включая добавленные через UI). Fallback на
  // встроенный список пока идёт загрузка.
  const { data: currenciesList } = useCurrencies();
  const ccyOptions = useMemo(() => {
    const codes = currenciesList?.length ? currenciesList.map((c) => c.code) : [...CCY_CODES];
    return codes.map((c) => ({ value: c, label: c }));
  }, [currenciesList]);
  const create = useCreateOrder();
  const currentUser = useAuthStore((s) => s.user);
  const [error, setError] = useState<string | null>(null);
  // «Намеренная» дельта от рынка, которую оператор накликал степпером/ввёл в кастом-инпут.
  // null = оператор сам вводил курс руками — показываем фактическую дельту, посчитанную из курса.
  // string = показываем эту дельту, не пересчитывая из округлённого курса (precision 4 даёт артефакты типа 0.94% вместо 1%).
  const [intendedPct, setIntendedPct] = useState<{ cl: string | null; ex: string | null }>({
    cl: null,
    ex: null,
  });

  const clients = useMemo(() => (accounts ?? []).filter((a) => a.kind === 'client').map((a) => a.code), [accounts]);
  const executors = useMemo(() => (accounts ?? []).filter((a) => a.kind === 'executor').map((a) => a.code), [accounts]);

  const ratesMap = useMemo(() => {
    const m: Partial<Record<CcyCode, Money>> = {};
    for (const r of rates ?? []) m[r.ccy] = r.rate;
    return m as Record<CcyCode, Money>;
  }, [rates]);

  const form = useForm<FormValues>({
    defaultValues: {
      client: '',
      executor: '',
      clGiveCcy: 'RUB',
      clGiveAmt: '',
      clRate: '',
      clGetCcy: 'CNY',
      clGetAmt: '',
      exGetCcy: 'USDT',
      exGetAmt: '',
      exRate: '',
      coefPct: '',
      fixUsdt: '',
      bonusUsdt: '',
      courierUsdt: '',
    },
  });
  // Режим курьера: 'extra' — сверх (клиент платит), 'included' — из суммы клиенту.
  const [courierMode, setCourierMode] = useState<'extra' | 'included'>('extra');

  const { watch, setValue, register, handleSubmit, getValues } = form;
  const v = watch();

  // Автокалькуляция клиента: 2 из 3 → третье. Вызывается на каждый onChange,
  // поэтому читаем актуальные values синхронно через getValues() (watch state
  // ещё не успел отрендериться).
  // После пересчёта клиента — каскадно пересчитываем exGetAmt (он зависит от
  // clGetAmt через exRate), чтобы вся форма обновлялась одной операцией.
  // ─── Recalc v2: происхождение значений (см. lib/recalc2.ts) ───
  // prov хранит src/seq каждого поля; значения живут в RHF. Инвариант:
  // введённое руками НИКОГДА не перезаписывается автоматически.
  const seqRef = useRef(0);
  const [prov, setProv] = useState<Record<ProvKey, ProvMeta>>(freshProv);
  const provRef = useRef(prov);
  provRef.current = prov;
  // Направление отображения курса: 'inv' показывает 1/canonical (для пар,
  // где канонический курс — мелкая дробь типа 0.0097 GBP за 1 RUB).
  const [rateDir, setRateDir] = useState<{ cl: RateDir; ex: RateDir }>({ cl: 'fwd', ex: 'fwd' });
  // Канонический курс, введённый оператором руками — база для %-степпера
  // («исполнитель дал 1.3386, накинь +1% комиссии»). null = базируемся на рынке.
  const manualRateBase = useRef<{ cl: string | null; ex: string | null }>({ cl: null, ex: null });

  // Фактический фикс курсов к USDT для профита ЭТОЙ сделки (перекрывает
  // справочник в снапшоте). Ключ — валюта, значение — курс. Пусто = справочник.
  const [rateFix, setRateFix] = useState<Record<string, string>>({});

  function buildClientState(cur: FormValues, p: Record<ProvKey, ProvMeta>): ClientState {
    return {
      give: { value: cur.clGiveAmt, ...p.clGive },
      get: { value: cur.clGetAmt, ...p.clGet },
      rate: { value: cur.clRate, ...p.clRate },
    };
  }

  function writeClient(out: ClientState, cur: FormValues, p: Record<ProvKey, ProvMeta>) {
    if (out.give.value !== cur.clGiveAmt) setValue('clGiveAmt', out.give.value, { shouldDirty: false });
    if (out.get.value !== cur.clGetAmt) setValue('clGetAmt', out.get.value, { shouldDirty: false });
    if (out.rate.value !== cur.clRate) setValue('clRate', out.rate.value, { shouldDirty: false });
    p.clGive = { src: out.give.src, seq: out.give.seq };
    p.clGet = { src: out.get.src, seq: out.get.seq };
    p.clRate = { src: out.rate.src, seq: out.rate.seq };
  }

  function cascadeExec(clGet: string, cur: FormValues, p: Record<ProvKey, ProvMeta>) {
    const state: ExecState = {
      amt: { value: cur.exGetAmt, ...p.exGet },
      rate: { value: cur.exRate, ...p.exRate },
    };
    const out = resolveExec(state, clGet, null);
    if (out.amt.value !== cur.exGetAmt) setValue('exGetAmt', out.amt.value, { shouldDirty: false });
    if (out.rate.value !== cur.exRate) setValue('exRate', out.rate.value, { shouldDirty: false });
    p.exGet = { src: out.amt.src, seq: out.amt.seq };
    p.exRate = { src: out.rate.src, seq: out.rate.seq };
  }

  /** Оператор отредактировал поле клиентской стороны. */
  function editClient(field: 'give' | 'get' | 'rate') {
    const cur = getValues();
    const p = { ...provRef.current };
    const provKey = ({ give: 'clGive', get: 'clGet', rate: 'clRate' } as const)[field];
    const value = field === 'give' ? cur.clGiveAmt : field === 'get' ? cur.clGetAmt : cur.clRate;
    // Очистка поля = «отпустить» (auto); ввод = ручная фиксация.
    p[provKey] = value === '' ? { src: 'auto', seq: 0 } : { src: 'user', seq: ++seqRef.current };
    const out = resolveClient(buildClientState(cur, p), field);
    writeClient(out, cur, p);
    cascadeExec(out.get.value, cur, p);
    setProv(p);
  }

  /** Оператор отредактировал поле исполнителя. */
  function editExec(field: 'amt' | 'rate') {
    const cur = getValues();
    const p = { ...provRef.current };
    const provKey = field === 'amt' ? 'exGet' : 'exRate';
    const value = field === 'amt' ? cur.exGetAmt : cur.exRate;
    p[provKey] = value === '' ? { src: 'auto', seq: 0 } : { src: 'user', seq: ++seqRef.current };
    const state: ExecState = {
      amt: { value: cur.exGetAmt, ...p.exGet },
      rate: { value: cur.exRate, ...p.exRate },
    };
    const out = resolveExec(state, cur.clGetAmt, field);
    if (out.amt.value !== cur.exGetAmt) setValue('exGetAmt', out.amt.value, { shouldDirty: false });
    if (out.rate.value !== cur.exRate) setValue('exRate', out.rate.value, { shouldDirty: false });
    p.exGet = { src: out.amt.src, seq: out.amt.seq };
    p.exRate = { src: out.rate.src, seq: out.rate.seq };
    setProv(p);
  }

  /** Клик по бейджу: запинить авто-поле / отпустить ручное (и пересчитать его). */
  function toggleProv(key: ProvKey) {
    const cur = getValues();
    const p = { ...provRef.current };
    if (p[key].src === 'auto') {
      // pin: значение остаётся, теперь оно неприкосновенно
      p[key] = { src: 'user', seq: ++seqRef.current };
      setProv(p);
      return;
    }
    // release: пересчитать поле из соседей, если возможно
    p[key] = { src: 'auto', seq: 0 };
    const give = D(cur.clGiveAmt || '0');
    const get = D(cur.clGetAmt || '0');
    const rate = D(cur.clRate || '0');
    const exGet = D(cur.exGetAmt || '0');
    const exRate = D(cur.exRate || '0');
    if (key === 'clGive' && get.gt(0) && rate.gt(0)) {
      setValue('clGiveAmt', get.dividedBy(rate).toFixed(AMT_UI_SCALE), { shouldDirty: false });
    } else if (key === 'clGet' && give.gt(0) && rate.gt(0)) {
      setValue('clGetAmt', give.times(rate).toFixed(AMT_UI_SCALE), { shouldDirty: false });
      cascadeExec(getValues().clGetAmt, getValues(), p);
    } else if (key === 'clRate' && give.gt(0) && get.gt(0)) {
      setValue('clRate', get.dividedBy(give).toDecimalPlaces(8).toString(), { shouldDirty: false });
    } else if (key === 'exGet' && get.gt(0) && exRate.gt(0)) {
      setValue('exGetAmt', get.dividedBy(exRate).toFixed(AMT_UI_SCALE), { shouldDirty: false });
    } else if (key === 'exRate' && get.gt(0) && exGet.gt(0)) {
      setValue('exRate', get.dividedBy(exGet).toDecimalPlaces(8).toString(), { shouldDirty: false });
    }
    setProv(p);
  }

  // ─── Авто-подстановка курсов по справочнику USDT-rates ───
  // Курс «1 единица A = X единиц B» = rate(B) / rate(A) (где rate — это
  // сколько единиц валюты в одном USDT).
  // marketClRate: clGiveCcy → clGetCcy (для clRate).
  // marketExRate: exGetCcy → clGetCcy (для exRate, "сколько clGetCcy за 1 exGetCcy").
  // 8 знаков, как канонический курс в state (см. RateInput): квантование до 4
  // здесь ломало мелкие кросс-курсы (RUB→GBP ≈ 0.0094 — потеря до 0.5%),
  // и ошибка уходила в автоподстановку сумм. До 4 курс режется на сабмите.
  const marketClRate = useMemo(() => {
    const a = ratesMap[v.clGiveCcy];
    const b = ratesMap[v.clGetCcy];
    if (!a || !b || D(a).isZero()) return null;
    return D(b).dividedBy(D(a)).toDecimalPlaces(8).toString();
  }, [ratesMap, v.clGiveCcy, v.clGetCcy]);

  const marketExRate = useMemo(() => {
    const a = ratesMap[v.exGetCcy];
    const b = ratesMap[v.clGetCcy];
    if (!a || !b || D(a).isZero()) return null;
    return D(b).dividedBy(D(a)).toDecimalPlaces(8).toString();
  }, [ratesMap, v.exGetCcy, v.clGetCcy]);

  // Любая кнопка ⟳ обновляет ВСЕ валюты текущей заявки разом — чтобы вся
  // форма стояла на одном снимке рынка. Раздельное обновление по парам давало
  // разные снапшоты при последовательных кликах (живой курс успевал сдвинуться).
  // USDT — знаменатель справочника (всегда 1), его не обновляем.
  const refreshAllCcys = useMemo(
    () => [...new Set([v.clGiveCcy, v.clGetCcy, v.exGetCcy])].filter((c) => c !== 'USDT'),
    [v.clGiveCcy, v.clGetCcy, v.exGetCcy],
  );

  // Смена валютной пары:
  //  - курс АВТО → подставляется рынок новой пары (как и раньше);
  //  - курс ВРУЧНУЮ → он сохраняется (оператор его задал — он главный),
  //    а суммы пересчитываются по нему для новой пары. Ничьи ручные значения
  //    не затираются молча — плавающее поле выбирает provenance.
  // Защита от refetch котировок: при НЕизменной паре ручной курс не трогается.
  const prevPair = useRef<{ cl: string; ex: string }>({ cl: '', ex: '' });

  useEffect(() => {
    const pair = `${v.clGiveCcy}|${v.clGetCcy}`;
    const pairChanged = pair !== prevPair.current.cl;
    prevPair.current.cl = pair;

    if (provRef.current.clRate.src === 'user') {
      if (!pairChanged) return;
      const cur = getValues();
      if (!cur.clRate || D(cur.clRate).lte(0)) return;
      // Ручной курс остаётся, суммы пересчитываются по нему.
      const p = { ...provRef.current };
      const out = resolveClient(buildClientState(cur, p), 'rate');
      writeClient(out, cur, p);
      cascadeExec(out.get.value, cur, p);
      setProv(p);
      return;
    }

    if (!marketClRate) return;
    if (v.clRate === marketClRate) return;
    const p = { ...provRef.current };
    p.clRate = { src: 'auto', seq: 0 };
    manualRateBase.current.cl = null;
    setValue('clRate', marketClRate, { shouldDirty: false });
    // Мелкий канонический курс (<1) читаемее в обратном направлении.
    setRateDir((d) => ({ ...d, cl: D(marketClRate).lt(1) ? 'inv' : 'fwd' }));
    const cur = { ...getValues(), clRate: marketClRate };
    const out = resolveClient(buildClientState(cur, p), 'rate');
    writeClient(out, cur, p);
    cascadeExec(out.get.value, cur, p);
    setProv(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketClRate, v.clGiveCcy, v.clGetCcy]);

  // Аналогично для курса исполнителя (пара exGetCcy|clGetCcy).
  useEffect(() => {
    const pair = `${v.exGetCcy}|${v.clGetCcy}`;
    const pairChanged = pair !== prevPair.current.ex;
    prevPair.current.ex = pair;

    if (provRef.current.exRate.src === 'user') {
      if (!pairChanged) return;
      const cur = getValues();
      if (!cur.exRate || D(cur.exRate).lte(0)) return;
      const p = { ...provRef.current };
      const state: ExecState = {
        amt: { value: cur.exGetAmt, ...p.exGet },
        rate: { value: cur.exRate, ...p.exRate },
      };
      const out = resolveExec(state, cur.clGetAmt, 'rate');
      if (out.amt.value !== cur.exGetAmt) setValue('exGetAmt', out.amt.value, { shouldDirty: false });
      p.exGet = { src: out.amt.src, seq: out.amt.seq };
      setProv(p);
      return;
    }

    if (!marketExRate) return;
    if (v.exRate === marketExRate) return;
    const p = { ...provRef.current };
    p.exRate = { src: 'auto', seq: 0 };
    manualRateBase.current.ex = null;
    setValue('exRate', marketExRate, { shouldDirty: false });
    setRateDir((d) => ({ ...d, ex: D(marketExRate).lt(1) ? 'inv' : 'fwd' }));
    const cur = { ...getValues(), exRate: marketExRate };
    const state: ExecState = {
      amt: { value: cur.exGetAmt, ...p.exGet },
      rate: { value: cur.exRate, ...p.exRate },
    };
    const out = resolveExec(state, cur.clGetAmt, 'rate');
    if (out.amt.value !== cur.exGetAmt) setValue('exGetAmt', out.amt.value, { shouldDirty: false });
    p.exGet = { src: out.amt.src, seq: out.amt.seq };
    setProv(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketExRate, v.exGetCcy, v.clGetCcy]);

  // Степпер/«к рынку» — явное действие оператора: курс становится ручным,
  // плавающее поле выбирается по provenance (recalc v2).
  function setRateAndRecalc(target: 'cl' | 'ex', newRate: string) {
    const r = D(newRate);
    if (r.lte(0)) return;
    const value = r.toDecimalPlaces(8).toString();
    if (target === 'cl') {
      setValue('clRate', value, { shouldDirty: true });
      editClient('rate');
    } else {
      setValue('exRate', value, { shouldDirty: true });
      editExec('rate');
    }
  }

  function applyMarketRate(target: 'cl' | 'ex') {
    const market = target === 'cl' ? marketClRate : marketExRate;
    if (!market) return;
    manualRateBase.current[target] = null; // база снова рыночная
    setRateAndRecalc(target, market);
    setIntendedPct((prev) => ({ ...prev, [target]: '0' }));
  }

  function applyRateDelta(target: 'cl' | 'ex', pct: number) {
    // База процента: курс, введённый оператором руками (кейс «исполнитель дал
    // 1.3386 от XE, накинь +1% комиссии»), иначе рыночный. Аддитивный
    // аккумулятор: источник истины — intendedPct, а не пересчёт из
    // округлённого курса (precision даёт артефакты).
    const manualBase = manualRateBase.current[target];
    const market = target === 'cl' ? marketClRate : marketExRate;
    const base = manualBase ?? market;
    const dir = rateDir[target];
    const accumPct = D(intendedPct[target] ?? '0').plus(pct);
    if (base) {
      // Процент применяется в ОТОБРАЖАЕМОМ направлении котировки: «+1%» для
      // оператора = «показанный курс больше на 1%». Для inv-направления это
      // деление канонического курса (1/((1/b)·k) = b/k).
      const k = D(1).plus(accumPct.dividedBy(100));
      const next = dir === 'inv' ? D(base).dividedBy(k) : D(base).times(k);
      setRateAndRecalc(target, next.toString());
    } else {
      const current = target === 'cl' ? v.clRate : v.exRate;
      const cur = D(current || '0');
      if (cur.gt(0)) {
        // Без рынка базой становится текущий курс — дальнейшие клики
        // аккумулируются аддитивно через intendedPct, а не компаундятся
        // мультипликативно (cur×1.01×1.01 расходился с показанной дельтой).
        const base = cur.toDecimalPlaces(8).toString();
        manualRateBase.current[target] = base;
        const k = D(1).plus(accumPct.dividedBy(100));
        const next = dir === 'inv' ? D(base).dividedBy(k) : D(base).times(k);
        setRateAndRecalc(target, next.toString());
      }
    }
    setIntendedPct((prev) => ({ ...prev, [target]: accumPct.toString() }));
  }

  /**
   * «Направление перепутано»: оператор ввёл число в обратной котировке
   * (например 1.3386 USDT/GBP при показанном GBP/USDT). Исправление: само
   * число остаётся как ввёл оператор, меняется его интерпретация —
   * канонический курс инвертируется, направление отображения переворачивается.
   */
  function fixRateDirection(target: 'cl' | 'ex') {
    const current = target === 'cl' ? getValues().clRate : getValues().exRate;
    const cur = D(current || '0');
    if (cur.lte(0)) return;
    const flipped = D(1).dividedBy(cur).toDecimalPlaces(8).toString();
    manualRateBase.current[target] = flipped;
    setRateDir((d) => ({ ...d, [target]: d[target] === 'fwd' ? 'inv' : 'fwd' }));
    if (target === 'cl') {
      setValue('clRate', flipped, { shouldDirty: true });
      clearIntendedPct('cl');
      editClient('rate');
    } else {
      setValue('exRate', flipped, { shouldDirty: true });
      clearIntendedPct('ex');
      editExec('rate');
    }
  }

  // Сбрасываем намеренную дельту при ручном редактировании курса — теперь
  // считаем реальную дельту из курса (а не показываем устаревший аккумулятор).
  function clearIntendedPct(target: 'cl' | 'ex') {
    setIntendedPct((prev) => (prev[target] === null ? prev : { ...prev, [target]: null }));
  }

  // Превью прибыли в USDT — тот же calcProfit, что и бэк (universal-USDT-модель).
  // exGiveCcy = clGetCcy (исполнитель отдаёт нам что клиент получает),
  // exGetCcy — выбирается оператором (по умолчанию USDT — реальный платёжный
  // флоу через крипто-пул). ratesMap передаём как snapshot.
  // Валюты, для которых имеет смысл фикс курса: участвуют в формуле профита
  // (clGive / exGet), кроме USDT (знаменатель) и кроме same-ccy сделок
  // (профит нативный, курсы не нужны).
  const profitFixCcys = useMemo(() => {
    if (v.clGiveCcy === v.exGetCcy) return [] as CcyCode[];
    return [...new Set([v.clGiveCcy, v.exGetCcy])].filter((c) => c !== 'USDT');
  }, [v.clGiveCcy, v.exGetCcy]);

  // Курсы для расчёта: справочник + активные фиксы этой сделки.
  const effectiveRates = useMemo(() => {
    const m = { ...ratesMap };
    for (const ccy of profitFixCcys) {
      const fix = rateFix[ccy];
      if (fix && D(fix).gt(0)) m[ccy] = fix;
    }
    return m;
  }, [ratesMap, rateFix, profitFixCcys]);

  // Итог коэффициента — финальная «к оплате клиентом» для превью (проводки,
  // FlowSummary, карточка «Получаем»). Профит — от базы (дельта транзитом).
  const coef = useMemo(() => coefResult(v.clGiveAmt, v.coefPct), [v.clGiveAmt, v.coefPct]);
  const clGiveFinalAmt = coef?.final ?? v.clGiveAmt;

  // Фикс за платёжку: задаётся в USDT, на каждой ноге конвертируется по курсу
  // (effectiveRates). Для превью/проводок прибавляется ПОСЛЕ коэффициента.
  const fix = useMemo(() => {
    if (!v.fixUsdt || !/^\d+(\.\d+)?$/.test(v.fixUsdt) || D(v.fixUsdt).lte(0)) return null;
    const clRate = effectiveRates[v.clGiveCcy];
    const exRate = effectiveRates[v.exGetCcy];
    if (!clRate || !exRate) return null;
    return {
      usdt: D(v.fixUsdt).toString(),
      cl: D(v.fixUsdt).times(D(clRate)).toDecimalPlaces(MONEY_SCALE).toString(),
      ex: D(v.fixUsdt).times(D(exRate)).toDecimalPlaces(MONEY_SCALE).toString(),
    };
  }, [v.fixUsdt, effectiveRates, v.clGiveCcy, v.exGetCcy]);
  const clGiveWithFix = fix ? dz(clGiveFinalAmt).plus(D(fix.cl)).toString() : clGiveFinalAmt;
  const exGetWithFix = fix && v.exGetAmt ? dz(v.exGetAmt).plus(D(fix.ex)).toString() : v.exGetAmt;
  // USDT-эквивалент фикса (симметричен на обеих ногах: fixCl/giveRate = fixEx/payRate = fixUsdt).
  const fixUsdtD = D(fix?.usdt ?? '0');

  // Бонус исполнителю: задаётся в USDT, конвертируется в валюту оплаты (exGetCcy)
  // по курсу и прибавляется ТОЛЬКО к ноге исполнителя ПОСЛЕ фикса. Клиента не
  // касается. Оплачивается из профита компании (в отличие от фикса-транзита).
  const bonus = useMemo(() => {
    if (!v.bonusUsdt || !/^\d+(\.\d+)?$/.test(v.bonusUsdt) || D(v.bonusUsdt).lte(0)) return null;
    const exRate = effectiveRates[v.exGetCcy];
    if (!exRate) return null;
    return {
      usdt: D(v.bonusUsdt).toString(),
      ex: D(v.bonusUsdt).times(D(exRate)).toDecimalPlaces(MONEY_SCALE).toString(),
    };
  }, [v.bonusUsdt, effectiveRates, v.exGetCcy]);
  const exGetWithBonus = bonus && v.exGetAmt ? dz(exGetWithFix).plus(D(bonus.ex)).toString() : exGetWithFix;
  const bonusUsdtD = D(bonus?.usdt ?? '0');

  // Курьер: фикс в USDT, целиком исполнителю. 'extra' — клиент доплачивает
  // (clGive += эквивалент, исполнителю idx2 += эквивалент, как фикс);
  // 'included' — клиент получает меньше (clGet −= эквивалент в clGetCcy),
  // исполнитель удерживает разницу из выдачи — exGet НЕ растёт.
  const courier = useMemo(() => {
    if (!v.courierUsdt || !/^\d+(\.\d+)?$/.test(v.courierUsdt) || D(v.courierUsdt).lte(0)) return null;
    if (courierMode === 'extra') {
      const clRate = effectiveRates[v.clGiveCcy];
      const exRate = effectiveRates[v.exGetCcy];
      if (!clRate || !exRate) return null;
      return {
        mode: 'extra' as const,
        usdt: D(v.courierUsdt).toString(),
        cl: D(v.courierUsdt).times(D(clRate)).toDecimalPlaces(MONEY_SCALE).toString(),
        ex: D(v.courierUsdt).times(D(exRate)).toDecimalPlaces(MONEY_SCALE).toString(),
      };
    }
    const getRate = effectiveRates[v.clGetCcy];
    if (!getRate) return null;
    return {
      mode: 'included' as const,
      usdt: D(v.courierUsdt).toString(),
      cl: D(v.courierUsdt).times(D(getRate)).toDecimalPlaces(MONEY_SCALE).toString(),
      ex: null,
    };
  }, [v.courierUsdt, courierMode, effectiveRates, v.clGiveCcy, v.exGetCcy, v.clGetCcy]);
  const clGiveWithCourier =
    courier?.mode === 'extra' ? dz(clGiveWithFix).plus(D(courier.cl)).toString() : clGiveWithFix;
  /** Фактическая выдача клиенту (в included уменьшена курьером). */
  const clGetEffective =
    courier?.mode === 'included' && v.clGetAmt ? dz(v.clGetAmt).minus(D(courier.cl)).toString() : v.clGetAmt;
  const exGetWithCourier =
    courier?.mode === 'extra' && v.exGetAmt ? dz(exGetWithBonus).plus(D(courier.ex!)).toString() : exGetWithBonus;
  // USDT-эквивалент курьера в extra-режиме (для «пути через USDT» — симметричен обеим ногам).
  const courierExtraUsdtD = D(courier?.mode === 'extra' ? courier.usdt : '0');

  const preview = useMemo(() => {
    const clGiveAmt = dz(v.clGiveAmt);
    const clGetAmt = dz(v.clGetAmt);
    const exGetAmt = dz(v.exGetAmt);
    if (!v.client || !v.executor) return null;
    if (clGiveAmt.lte(0) || clGetAmt.lte(0) || exGetAmt.lte(0)) return null;
    const giveRate = effectiveRates[v.clGiveCcy];
    const payRate = effectiveRates[v.exGetCcy];
    if (!giveRate || !payRate) return null;
    try {
      const result = calcProfit({
        clGiveCcy: v.clGiveCcy,
        clGiveAmt: clGiveAmt.toString(),
        clGetCcy: v.clGetCcy,
        clGetAmt: clGetAmt.toString(),
        exGiveCcy: v.clGetCcy,
        exGiveAmt: clGetAmt.toString(),
        exGetCcy: v.exGetCcy,
        exGetAmt: exGetAmt.toString(),
        rates: effectiveRates,
        // Бонус (в USDT) вычитается из профита → превью показывает нетто.
        bonusUsdt: bonus?.usdt,
      });
      const receiveUsdt = D(result.receiveUsdt); // от БАЗЫ — участвует в профите
      const payUsdt = D(result.payUsdt);
      const profitUsdt = D(result.amount);
      const profitPct = receiveUsdt.gt(0) ? profitUsdt.dividedBy(receiveUsdt).times(100) : D(0);
      // Реально получаем от клиента — с коэффициентом; дельта — транзит рефереру.
      const receiveUsdtFinal =
        v.clGiveCcy === 'USDT' ? D(clGiveFinalAmt) : D(clGiveFinalAmt).dividedBy(D(giveRate));
      const coefUsdt = receiveUsdtFinal.minus(receiveUsdt);
      // ε-tolerance: |profit| < 0.005 USDT — это шум округления промежуточных
      // .toFixed(2) в recalc'е, а не реальный убыток. Считаем безубытком.
      const isNearZero = profitUsdt.abs().lt('0.005');
      return {
        receiveUsdt,
        receiveUsdtFinal,
        coefUsdt,
        payUsdt,
        profitUsdt,
        profitPct,
        isLoss: !isNearZero && profitUsdt.isNegative(),
        isNearZero,
        nativeAmount: result.nativeAmount ?? null,
        nativeCcy: result.nativeCcy ?? null,
        giveRate,
        payRate,
      };
    } catch {
      return null;
    }
  }, [v.client, v.executor, v.clGiveAmt, v.clGetAmt, v.exGetAmt, v.clGiveCcy, v.clGetCcy, v.exGetCcy, effectiveRates, clGiveFinalAmt, bonus]);

  const provodkiPreview = useMemo(() => {
    if (!v.client || !v.executor || !v.clGiveAmt || !v.clGetAmt || !v.exGetAmt) return null;
    return [
      { label: `Клиент (${v.client})`, ccy: v.clGiveCcy, amt: dz(clGiveWithCourier).neg().toString(), desc: `клиент нам должен ${fmtAmt(clGiveWithCourier, v.clGiveCcy)}` },
      { label: `Клиент (${v.client})`, ccy: v.clGetCcy, amt: clGetEffective, desc: `мы должны клиенту ${fmtAmt(clGetEffective, v.clGetCcy)}` },
      { label: `Исполнитель (${v.executor})`, ccy: v.exGetCcy, amt: exGetWithCourier, desc: `мы должны исполнителю ${fmtAmt(exGetWithCourier, v.exGetCcy)}` },
      { label: `Исполнитель (${v.executor})`, ccy: v.clGetCcy, amt: dz(clGetEffective).neg().toString(), desc: `исполнитель должен нам ${fmtAmt(clGetEffective, v.clGetCcy)}` },
    ];
  }, [v.client, v.executor, v.clGiveAmt, v.clGetAmt, v.exGetAmt, v.clGiveCcy, v.clGetCcy, v.exGetCcy, clGiveFinalAmt, clGiveWithCourier, clGetEffective, exGetWithCourier]);

  // ─── Разборы «как получилась цифра» (FormulaHint) ───
  // ВАЖНО: новое поле, влияющее на суммы/профит, обязано попасть и сюда —
  // в термы соответствующих разборов (см. lib/breakdown.ts и CLAUDE.md).
  const pos = (s: string) => !!s && /^\d+(\.\d+)?$/.test(s) && D(s).gt(0);

  // Автопересчитанные суммы: ƒ показываем только у полей с src='auto'.
  // rateDir прокидывается в разбор — курс там показывается В ТОЙ ЖЕ котировке,
  // что и в поле (иначе менеджер видит «перевёрнутый» курс и дельту рынка
  // с противоположным знаком).
  const clGiveHint = useMemo(() => {
    if (prov.clGive.src !== 'auto' || !pos(v.clGiveAmt) || !pos(v.clGetAmt) || !pos(v.clRate)) return null;
    return bdPairAmount({
      mode: 'give-from-get',
      giveAmt: v.clGiveAmt,
      getAmt: v.clGetAmt,
      rate: v.clRate,
      giveCcy: v.clGiveCcy,
      getCcy: v.clGetCcy,
      rateDir: rateDir.cl,
      marketRate: marketClRate,
      fieldRefs: { give: 'clGiveAmt', get: 'clGetAmt', rate: 'clRate' },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prov.clGive.src, v.clGiveAmt, v.clGetAmt, v.clRate, v.clGiveCcy, v.clGetCcy, rateDir.cl, marketClRate]);

  const clGetHint = useMemo(() => {
    if (prov.clGet.src !== 'auto' || !pos(v.clGiveAmt) || !pos(v.clGetAmt) || !pos(v.clRate)) return null;
    return bdPairAmount({
      mode: 'get-from-give',
      giveAmt: v.clGiveAmt,
      getAmt: v.clGetAmt,
      rate: v.clRate,
      giveCcy: v.clGiveCcy,
      getCcy: v.clGetCcy,
      rateDir: rateDir.cl,
      marketRate: marketClRate,
      fieldRefs: { give: 'clGiveAmt', get: 'clGetAmt', rate: 'clRate' },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prov.clGet.src, v.clGiveAmt, v.clGetAmt, v.clRate, v.clGiveCcy, v.clGetCcy, rateDir.cl, marketClRate]);

  const exGetHint = useMemo(() => {
    if (prov.exGet.src !== 'auto' || !pos(v.clGetAmt) || !pos(v.exRate)) return null;
    return bdExecAuto({
      clGetAmt: v.clGetAmt,
      exRate: v.exRate,
      clGetCcy: v.clGetCcy,
      exGetCcy: v.exGetCcy,
      rateDir: rateDir.ex,
      marketRate: marketExRate,
      fieldRefs: { clGet: 'clGetAmt', exRate: 'exRate' },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prov.exGet.src, v.clGetAmt, v.exRate, v.clGetCcy, v.exGetCcy, rateDir.ex, marketExRate]);

  // Итог клиента «Получаем от клиента»: база × (1+коэф) + фикс + курьер-«сверх».
  const clientTotalHint = useMemo(() => {
    if (!pos(v.clGiveAmt)) return null;
    const coefValid = v.coefPct !== '' && /^-?\d+(\.\d+)?$/.test(v.coefPct) && !D(v.coefPct).isZero();
    return bdClientTotal({
      baseAmt: v.clGiveAmt,
      coefPct: coefValid ? v.coefPct : null,
      fixUsdt: fix?.usdt ?? null,
      courierUsdt: courier?.mode === 'extra' ? courier.usdt : null,
      rateForCcy: effectiveRates[v.clGiveCcy] ?? null,
      ccy: v.clGiveCcy,
      rateBadge: 'рынок',
      fieldRefs: { base: 'clGiveAmt', coef: 'coefPct', fix: 'fixUsdt', courier: 'courierUsdt' },
    });
  }, [v.clGiveAmt, v.coefPct, fix, courier, effectiveRates, v.clGiveCcy]);

  // Итог исполнителя «Платим исполнителю»: обмен + фикс + бонус + курьер-«сверх».
  const execTotalHint = useMemo(() => {
    if (!pos(v.exGetAmt)) return null;
    return bdExecutorTotal({
      exchangeAmt: v.exGetAmt,
      fixUsdt: fix?.usdt ?? null,
      bonusUsdt: bonus?.usdt ?? null,
      courierUsdt: courier?.mode === 'extra' ? courier.usdt : null,
      courierIncludedUsdt: courier?.mode === 'included' ? courier.usdt : null,
      rateForCcy: effectiveRates[v.exGetCcy] ?? null,
      ccy: v.exGetCcy,
      rateBadge: 'рынок',
      fieldRefs: { exchange: 'exGetAmt', fix: 'fixUsdt', bonus: 'bonusUsdt', courier: 'courierUsdt' },
    });
  }, [v.exGetAmt, fix, bonus, courier, effectiveRates, v.exGetCcy]);

  // Профит: боевой calcProfit + активные проверки (курсы против рынка, знак).
  const profitHint = useMemo(() => {
    if (!preview) return null;
    try {
      return bdProfit({
        input: {
          clGiveCcy: v.clGiveCcy,
          clGiveAmt: dz(v.clGiveAmt).toString(),
          clGetCcy: v.clGetCcy,
          clGetAmt: dz(v.clGetAmt).toString(),
          exGiveCcy: v.clGetCcy,
          exGiveAmt: dz(v.clGetAmt).toString(),
          exGetCcy: v.exGetCcy,
          exGetAmt: dz(v.exGetAmt).toString(),
          rates: effectiveRates,
          bonusUsdt: bonus?.usdt,
        },
        ratesBadge: 'рынок',
        fieldRefs: { bonus: 'bonusUsdt' },
        checks: buildProfitChecks({
          profitUsdt: preview.profitUsdt.toString(),
          marginPct: preview.profitPct.toString(),
          rateChecks: [
            ...buildRateChecks({ target: 'cl', label: 'Курс клиента', rate: v.clRate, market: marketClRate }),
            ...buildRateChecks({ target: 'ex', label: 'Курс исполнителя', rate: v.exRate, market: marketExRate }),
          ],
        }),
      });
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, v.clGiveCcy, v.clGiveAmt, v.clGetCcy, v.clGetAmt, v.exGetCcy, v.exGetAmt, v.clRate, v.exRate, effectiveRates, bonus, marketClRate, marketExRate]);

  function onProfitCheckAction(id: 'flip-cl' | 'flip-ex') {
    fixRateDirection(id === 'flip-cl' ? 'cl' : 'ex');
  }

  function close() {
    navigate(-1);
  }

  async function onSubmit(values: FormValues) {
    setError(null);
    if (!values.client || !values.executor) {
      setError('Выберите клиента и исполнителя');
      return;
    }
    // Оператор = telegram-username юзера (приоритет) или его email-логин.
    const operator = currentUser?.telegramUsername || currentUser?.email;
    if (!operator) {
      setError('Не удалось определить оператора — войдите в систему заново');
      return;
    }
    const giveAmt = D(values.clGiveAmt || '0');
    const getAmt = D(values.clGetAmt || '0');
    const exGetAmt = D(values.exGetAmt || '0');
    if (giveAmt.lte(0) || getAmt.lte(0) || exGetAmt.lte(0)) {
      setError('Заполните все суммы (> 0)');
      return;
    }
    // ε-проверка согласованности: при provenance-пересчёте рассинхрон возможен
    // только если оператор запинил все три поля с противоречивыми значениями.
    if (values.clRate && D(values.clRate).gt(0)) {
      const implied = giveAmt.times(D(values.clRate));
      const drift = implied.minus(getAmt).abs().dividedBy(getAmt);
      if (drift.gt('0.01')) {
        setError(
          `Курс для клиента не сходится с суммами: ${giveAmt} × ${values.clRate} ≈ ${implied.toFixed(2)}, а указано ${getAmt}. ` +
            'Отпусти одно из полей (клик по бейджу «✎») или поправь значения.',
        );
        return;
      }
    }
    // Коэффициент: на сервер уходит БАЗА + процент, умножает сервер.
    let coefPct: string | undefined;
    if (values.coefPct !== '' && /^-?\d+(\.\d+)?$/.test(values.coefPct) && !D(values.coefPct).isZero()) {
      const p = D(values.coefPct);
      if (p.lte(-100) || p.gt(100)) {
        setError('Коэффициент должен быть в диапазоне от −100 до 100');
        return;
      }
      coefPct = p.toDecimalPlaces(MONEY_SCALE).toString();
    }
    // Фикс за платёжку: на сервер уходит сумма в USDT, конвертацию делает сервер.
    let fixUsdt: string | undefined;
    if (values.fixUsdt !== '' && /^\d+(\.\d+)?$/.test(values.fixUsdt) && D(values.fixUsdt).gt(0)) {
      fixUsdt = D(values.fixUsdt).toDecimalPlaces(MONEY_SCALE).toString();
    }
    // Бонус исполнителю: на сервер уходит сумма в USDT, конвертацию/вычет из
    // профита делает сервер (server-of-truth).
    let bonusUsdt: string | undefined;
    if (values.bonusUsdt !== '' && /^\d+(\.\d+)?$/.test(values.bonusUsdt) && D(values.bonusUsdt).gt(0)) {
      bonusUsdt = D(values.bonusUsdt).toDecimalPlaces(MONEY_SCALE).toString();
    }
    // Курьер: на сервер уходит сумма в USDT + режим, конвертацию и корректировку
    // ног делает сервер (server-of-truth, как коэффициент/фикс).
    let courierUsdtOut: string | undefined;
    if (values.courierUsdt !== '' && /^\d+(\.\d+)?$/.test(values.courierUsdt) && D(values.courierUsdt).gt(0)) {
      courierUsdtOut = D(values.courierUsdt).toDecimalPlaces(MONEY_SCALE).toString();
      if (courierMode === 'included' && dz(clGetEffective).lte(0)) {
        setError('Курьер «из суммы» больше суммы клиенту — уменьшите курьер');
        return;
      }
    }
    const payload: CreateOrderInput = {
      client: values.client,
      executor: values.executor,
      clGiveCcy: values.clGiveCcy,
      // Суммы квантуем до точности хранения программно — не полагаемся на
      // браузерную step-валидацию (значение могло попасть в state иначе).
      clGiveAmt: giveAmt.toDecimalPlaces(MONEY_SCALE).toString(),
      coefPct,
      fixUsdt,
      bonusUsdt,
      courierUsdt: courierUsdtOut,
      courierMode: courierUsdtOut ? courierMode : undefined,
      clGetCcy: values.clGetCcy,
      clGetAmt: getAmt.toDecimalPlaces(MONEY_SCALE).toString(),
      // Курсы храним канонически с 8 знаками в форме, API принимает ≤4 (MoneyStr).
      clRate: values.clRate ? D(values.clRate).toDecimalPlaces(MONEY_SCALE).toString() : null,
      // exGiveCcy = clGetCcy (то что клиент получает = исполнитель отдаёт нам)
      exGiveCcy: values.clGetCcy,
      exGiveAmt: getAmt.toDecimalPlaces(MONEY_SCALE).toString(),
      // exGetCcy выбирается оператором (по умолчанию USDT — мы платим исполнителю
      // в крипте, а он закупает нужную клиенту валюту на своей стороне).
      exGetCcy: values.exGetCcy,
      exGetAmt: exGetAmt.toDecimalPlaces(MONEY_SCALE).toString(),
      exRate: values.exRate ? D(values.exRate).toDecimalPlaces(MONEY_SCALE).toString() : null,
      operator: operator as `@${string}`,
      // Дата = момент создания (бизнес-дата заявки).
      date: today(),
    };
    // Фактический фикс курсов профита — только валидные введённые значения.
    const overrides: Record<string, string> = {};
    for (const ccy of profitFixCcys) {
      const fix = rateFix[ccy];
      if (fix && D(fix).gt(0)) overrides[ccy] = D(fix).toDecimalPlaces(MONEY_SCALE).toString();
    }
    if (Object.keys(overrides).length > 0) payload.rateOverrides = overrides;
    try {
      const created = await create.mutateAsync(payload);
      navigate(`/orders/${created.id}`, { replace: true });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Modal title="Новая заявка" onClose={close} size="xl">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <fieldset className="rounded-lg border border-border bg-bg2 p-4">
            <legend className="px-2 text-xs font-semibold uppercase tracking-wide text-text2">Клиент</legend>
            <div className="mb-3">
              <Combobox
                value={v.client}
                onChange={(val) => setValue('client', val, { shouldDirty: true })}
                options={clients.map((c) => ({ value: c, label: c }))}
                placeholder="— выберите клиента —"
              />
            </div>

            <FlowSummary
              role="client"
              receive={{
                // РЕАЛЬНЫЙ поток от клиента: база × коэф + фикс + курьер-«сверх».
                // Гвард на пустую базу — иначе при одном лишь фиксе показывался бы
                // «получаем 81 470 ₽» без введённой суммы.
                amt: dz(v.clGiveAmt).gt(0) ? clGiveWithCourier : v.clGiveAmt,
                ccy: v.clGiveCcy,
              }}
              send={{ amt: clGetEffective, ccy: v.clGetCcy }}
            />

            <div className="grid grid-cols-2 gap-2">
              <Field label="Отдаёт нам" hint="клиент пришлёт нам">
                <Combobox
                  value={v.clGiveCcy}
                  onChange={(val) => setValue('clGiveCcy', val as CcyCode, { shouldDirty: true })}
                  options={ccyOptions}
                  searchable={false}
                />
              </Field>
              <Field
                label="Сумма"
                reserveHintSpace
                fhRef="clGiveAmt"
                labelExtra={
                  <>
                    {clGiveHint && <FormulaHint formula={clGiveHint} />}
                    {v.clGiveAmt !== '' && (
                      <ProvBadge meta={prov.clGive} onToggle={() => toggleProv('clGive')} />
                    )}
                  </>
                }
              >
                <input
                  type="number"
                  step={AMT_UI_STEP}
                  {...register('clGiveAmt', { onChange: () => editClient('give') })}
                  className={`${inputCls} ${noSpinCls} font-mono tabular-nums`}
                  placeholder="0"
                />
              </Field>
            </div>
            <Field
              label="Курс для клиента"
              fhRef="clRate"
              hint={
                rateDir.cl === 'fwd'
                  ? `сколько ${v.clGetCcy} за 1 ${v.clGiveCcy}`
                  : `сколько ${v.clGiveCcy} за 1 ${v.clGetCcy}`
              }
              labelExtra={
                <>
                  <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setRateDir((d) => ({ ...d, cl: d.cl === 'fwd' ? 'inv' : 'fwd' }))}
                      title={`Курс показан: сколько ${rateDir.cl === 'fwd' ? v.clGetCcy : v.clGiveCcy} за 1 ${rateDir.cl === 'fwd' ? v.clGiveCcy : v.clGetCcy}. Клик — перевернуть котировку`}
                      className="rounded-full border border-border-strong bg-bg px-1.5 py-px font-mono text-[9px] font-bold leading-[14px] text-text2 transition-colors duration-150 hover:border-accent hover:text-accent"
                    >
                      {rateDir.cl === 'fwd' ? `${v.clGetCcy}/${v.clGiveCcy}` : `${v.clGiveCcy}/${v.clGetCcy}`} ⇄
                    </button>
                  {v.clRate !== '' && (
                    <ProvBadge meta={prov.clRate} onToggle={() => toggleProv('clRate')} />
                  )}
                </>
              }
            >
              <RateInput
                canonical={v.clRate}
                dir={rateDir.cl}
                onCanonicalChange={(c) => {
                  manualRateBase.current.cl = c || null;
                  setValue('clRate', c, { shouldDirty: true });
                  clearIntendedPct('cl');
                  editClient('rate');
                }}
                className={`${inputCls} ${noSpinCls} font-mono tabular-nums`}
                placeholder="0"
              />
              <RateHint
                marketRate={marketClRate}
                currentRate={v.clRate}
                intendedDeltaPct={intendedPct.cl}
                dir={rateDir.cl}
                pair={
                  rateDir.cl === 'fwd'
                    ? `${v.clGiveCcy}→${v.clGetCcy}`
                    : `${v.clGetCcy}→${v.clGiveCcy}`
                }
                refreshCcys={refreshAllCcys}
                onApply={() => applyMarketRate('cl')}
                onDelta={(pct) => applyRateDelta('cl', pct)}
                onFlipFix={() => fixRateDirection('cl')}
              />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Получает от нас" hint="мы отправим клиенту">
                <Combobox
                  value={v.clGetCcy}
                  onChange={(val) => setValue('clGetCcy', val as CcyCode, { shouldDirty: true })}
                  options={ccyOptions}
                  searchable={false}
                />
              </Field>
              <Field
                label="Сумма"
                reserveHintSpace
                fhRef="clGetAmt"
                labelExtra={
                  <>
                    {clGetHint && <FormulaHint formula={clGetHint} />}
                    {v.clGetAmt !== '' && (
                      <ProvBadge meta={prov.clGet} onToggle={() => toggleProv('clGet')} />
                    )}
                  </>
                }
              >
                <input
                  type="number"
                  step={AMT_UI_STEP}
                  {...register('clGetAmt', { onChange: () => editClient('get') })}
                  className={`${inputCls} ${noSpinCls} font-mono tabular-nums`}
                  placeholder="0"
                />
              </Field>
            </div>

            {/* Коэффициент: применяется к «отдаёт нам» ПОСЛЕ расчёта курса,
                в пересчёт give×rate=get не входит. Дельта — транзит рефереру. */}
            <div className="mt-3 border-t border-border-subtle pt-3">
              <div className="grid grid-cols-2 items-end gap-2">
                <Field
                  fhRef="coefPct"
                  label={
                    <span className="inline-flex items-center gap-1">
                      Коэффициент, %
                      <HelpTip>
                        Наценка (или скидка при минусе) на сумму «отдаёт нам» — применяется{' '}
                        <strong>после</strong> расчёта курса, поэтому курс перестаёт сходиться с
                        суммами. Дельта транзитом уходит приведшему клиента и в профит не входит.
                        Диапазон: от −100 до 100.
                      </HelpTip>
                    </span>
                  }
                >
                  <input
                    type="number"
                    step="0.01"
                    {...register('coefPct')}
                    className={`${inputCls} ${noSpinCls} font-mono tabular-nums`}
                    placeholder="0"
                  />
                </Field>
                {coef && (
                  <div className="pb-1 text-xs leading-relaxed text-text2">
                    К оплате клиентом:{' '}
                    <span className="font-mono font-semibold text-text">
                      {fmtAmt(coef.final, v.clGiveCcy)}
                    </span>
                    <br />
                    вкл. коэффициент {D(v.coefPct).gt(0) ? '+' : ''}
                    {D(v.coefPct).toString()}% ={' '}
                    <span className="font-mono">{fmtAmt(coef.delta, v.clGiveCcy)}</span>
                  </div>
                )}
              </div>
            </div>
          </fieldset>

          <fieldset className="rounded-lg border border-border bg-bg2 p-4">
            <legend className="px-2 text-xs font-semibold uppercase tracking-wide text-text2">Исполнитель</legend>
            <div className="mb-3">
              <Combobox
                value={v.executor}
                onChange={(val) => setValue('executor', val, { shouldDirty: true })}
                options={executors.map((c) => ({ value: c, label: c }))}
                placeholder="— выберите исполнителя —"
              />
            </div>

            <FlowSummary
              role="executor"
              receive={{ amt: clGetEffective, ccy: v.clGetCcy }}
              // РЕАЛЬНЫЙ поток исполнителю: обмен + фикс + бонус + курьер-«сверх»
              // (все производные внутри защищены гвардом на пустой exGetAmt).
              send={{ amt: exGetWithCourier, ccy: v.exGetCcy }}
            />

            <div className="grid grid-cols-2 gap-2">
              <Field
                label="Платим в"
                hint={v.exGetCcy === 'USDT' ? 'через USDT-пул' : 'прямой платёж'}
              >
                <Combobox
                  value={v.exGetCcy}
                  onChange={(val) => setValue('exGetCcy', val as CcyCode, { shouldDirty: true })}
                  options={ccyOptions}
                  searchable={false}
                />
              </Field>
              <Field
                label="Сумма"
                reserveHintSpace
                fhRef="exGetAmt"
                labelExtra={
                  <>
                    {exGetHint && <FormulaHint formula={exGetHint} />}
                    {v.exGetAmt !== '' && (
                      <ProvBadge meta={prov.exGet} onToggle={() => toggleProv('exGet')} />
                    )}
                  </>
                }
              >
                <input
                  type="number"
                  step={AMT_UI_STEP}
                  {...register('exGetAmt', { onChange: () => editExec('amt') })}
                  className={`${inputCls} ${noSpinCls} font-mono tabular-nums`}
                  placeholder="Сумма исполнителю"
                />
              </Field>
            </div>
            <Field
              label="Курс исполнителя"
              fhRef="exRate"
              hint={
                rateDir.ex === 'fwd'
                  ? `сколько ${v.clGetCcy} за 1 ${v.exGetCcy} (наша закупка)`
                  : `сколько ${v.exGetCcy} за 1 ${v.clGetCcy} (наша закупка)`
              }
              labelExtra={
                <>
                  <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setRateDir((d) => ({ ...d, ex: d.ex === 'fwd' ? 'inv' : 'fwd' }))}
                      title={`Курс показан: сколько ${rateDir.ex === 'fwd' ? v.clGetCcy : v.exGetCcy} за 1 ${rateDir.ex === 'fwd' ? v.exGetCcy : v.clGetCcy}. Клик — перевернуть котировку`}
                      className="rounded-full border border-border-strong bg-bg px-1.5 py-px font-mono text-[9px] font-bold leading-[14px] text-text2 transition-colors duration-150 hover:border-accent hover:text-accent"
                    >
                      {rateDir.ex === 'fwd' ? `${v.clGetCcy}/${v.exGetCcy}` : `${v.exGetCcy}/${v.clGetCcy}`} ⇄
                    </button>
                  {v.exRate !== '' && (
                    <ProvBadge meta={prov.exRate} onToggle={() => toggleProv('exRate')} />
                  )}
                </>
              }
            >
              <RateInput
                canonical={v.exRate}
                dir={rateDir.ex}
                onCanonicalChange={(c) => {
                  manualRateBase.current.ex = c || null;
                  setValue('exRate', c, { shouldDirty: true });
                  clearIntendedPct('ex');
                  editExec('rate');
                }}
                className={`${inputCls} ${noSpinCls} font-mono tabular-nums`}
                placeholder="необязательно"
              />
              <RateHint
                marketRate={marketExRate}
                currentRate={v.exRate}
                intendedDeltaPct={intendedPct.ex}
                dir={rateDir.ex}
                pair={
                  rateDir.ex === 'fwd'
                    ? `${v.exGetCcy}→${v.clGetCcy}`
                    : `${v.clGetCcy}→${v.exGetCcy}`
                }
                refreshCcys={refreshAllCcys}
                onApply={() => applyMarketRate('ex')}
                onDelta={(pct) => applyRateDelta('ex', pct)}
                onFlipFix={() => fixRateDirection('ex')}
              />
            </Field>

            {/* Бонус исполнителю: благодарность за проведённую сделку. Только в
                USDT, прибавляется ТОЛЬКО к оплате исполнителю (клиента не касается)
                и вычитается из профита — в отличие от фикса это деньги компании. */}
            <div className="mt-3 border-t border-border-subtle pt-3">
              <div className="grid grid-cols-2 items-end gap-2">
                <Field
                  fhRef="fixUsdt"
                  label={
                    <span className="inline-flex items-center gap-1">
                      Фикс за платёжку, USDT
                      <HelpTip>
                        Фикс-комиссия за выпуск платежа: берём с клиента и отдаём
                        исполнителю <strong>1:1</strong>, на профит не влияет. Задаётся в USDT;
                        если валюта ноги другая — конвертируется по курсу сделки.
                      </HelpTip>
                    </span>
                  }
                >
                  <input
                    type="number"
                    step={AMT_UI_STEP}
                    {...register('fixUsdt')}
                    className={`${inputCls} ${noSpinCls} font-mono tabular-nums`}
                    placeholder="0"
                  />
                </Field>
                {fix ? (
                  <div className="pb-1 text-xs leading-relaxed text-text2">
                    Фикс {fmtAmt(fix.usdt, 'USDT')} USDT транзитом:
                    <br />
                    клиент +<span className="font-mono">{fmtAmt(fix.cl, v.clGiveCcy)}</span>,
                    исполнитель +<span className="font-mono">{fmtAmt(fix.ex, v.exGetCcy)}</span>
                  </div>
                ) : (
                  <div />
                )}
                <Field
                  fhRef="bonusUsdt"
                  label={
                    <span className="inline-flex items-center gap-1">
                      Бонус исполнителю, USDT
                      <HelpTip>
                        Благодарность исполнителю за проведённую сделку. Прибавляется{' '}
                        <strong>только</strong> к оплате исполнителю (клиента не касается) и{' '}
                        <strong>вычитается из профита</strong> — в отличие от фикса это деньги
                        компании. Задаётся в USDT; если валюта оплаты другая — конвертируется по
                        курсу сделки.
                      </HelpTip>
                    </span>
                  }
                >
                  <input
                    type="number"
                    step={AMT_UI_STEP}
                    {...register('bonusUsdt')}
                    className={`${inputCls} ${noSpinCls} font-mono tabular-nums`}
                    placeholder="0"
                  />
                </Field>
                {bonus && (
                  <div className="pb-1 text-xs leading-relaxed text-text2">
                    Бонус {fmtAmt(bonus.usdt, 'USDT')} USDT из профита:
                    <br />
                    исполнитель +<span className="font-mono">{fmtAmt(bonus.ex, v.exGetCcy)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Курьер: фикс в USDT, целиком исполнителю. Переключатель — кто платит:
                сверх (клиент доплачивает) или из суммы (клиент получает меньше,
                исполнитель удерживает разницу из выдачи). Профит не меняется. */}
            <div className="mt-3 border-t border-border-subtle pt-3">
              <div className="grid grid-cols-2 items-end gap-2">
                <Field
                  fhRef="courierUsdt"
                  label={
                    <span className="inline-flex items-center gap-1">
                      Курьер, USDT
                      <HelpTip>
                        Курьерский фикс — целиком отходит исполнителю. Переключатель определяет,
                        кто платит: <strong>сверх</strong> — клиент доплачивает (к «отдаёт нам»
                        добавится эквивалент); <strong>из суммы</strong> — клиент получит меньше
                        (из «получает от нас» вычтется эквивалент), исполнитель удержит разницу
                        из выдачи. Профит компании не меняется в обоих режимах.
                      </HelpTip>
                    </span>
                  }
                >
                  <input
                    type="number"
                    step={AMT_UI_STEP}
                    {...register('courierUsdt')}
                    className={`${inputCls} ${noSpinCls} font-mono tabular-nums`}
                    placeholder="0"
                  />
                </Field>
                {/* Сегмент высотой с инпут (h-9), лейблы однострочные — полное
                    объяснение режимов в HelpTip и живой подписи под полем. */}
                <Field label="Откуда берём">
                  <div className="inline-flex h-9 overflow-hidden rounded-md border border-border-strong">
                    <button
                      type="button"
                      onClick={() => setCourierMode('extra')}
                      className={`px-3 text-sm font-medium whitespace-nowrap transition-colors duration-150 ${
                        courierMode === 'extra'
                          ? 'bg-accent text-white'
                          : 'bg-bg text-text2 hover:bg-bg2'
                      }`}
                    >
                      Сверх
                    </button>
                    <button
                      type="button"
                      onClick={() => setCourierMode('included')}
                      className={`border-l border-border-strong px-3 text-sm font-medium whitespace-nowrap transition-colors duration-150 ${
                        courierMode === 'included'
                          ? 'bg-accent text-white'
                          : 'bg-bg text-text2 hover:bg-bg2'
                      }`}
                    >
                      Из суммы
                    </button>
                  </div>
                </Field>
              </div>
              {courier && (
                <div className="mt-1.5 text-xs leading-relaxed text-text2">
                  {courier.mode === 'extra' ? (
                    <>
                      Курьер {fmtAmt(courier.usdt, 'USDT')} USDT сверх: клиент доплатит{' '}
                      <span className="font-mono">+{fmtAmt(courier.cl, v.clGiveCcy)}</span>,
                      исполнителю <span className="font-mono">+{fmtAmt(courier.ex!, v.exGetCcy)}</span>.
                      Профит не меняется.
                    </>
                  ) : (
                    <>
                      Курьер {fmtAmt(courier.usdt, 'USDT')} USDT из суммы: клиент получит{' '}
                      <span className="font-mono">−{fmtAmt(courier.cl, v.clGetCcy)}</span>
                      {/* «итого» — только когда сумма клиенту уже заполнена: fmtAmt('')
                          кидает из decimal.js → белый экран (курьер вводят и до сумм). */}
                      {v.clGetAmt !== '' && dz(v.clGetAmt).gt(0) && (
                        <> (итого {fmtAmt(dz(clGetEffective).toString(), v.clGetCcy)})</>
                      )}{' '}
                      — разницу исполнитель удержит из выдачи. Профит не меняется.
                    </>
                  )}
                </div>
              )}
            </div>
          </fieldset>
        </div>

        {profitFixCcys.length > 0 && (
          <div className="rounded-lg border border-border bg-bg2 p-4">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-text2">
                {profitFixCcys.length === 1
                  ? `Курс конвертации ${profitFixCcys[0]} → USDT`
                  : 'Курсы конвертации в USDT'}
              </span>
              <HelpTip>
                <p>
                  Профит сделки считается в USDT: полученное от клиента сравнивается с уплаченным
                  исполнителю. Для этого {profitFixCcys.join(' и ')} переводятся в USDT по курсу ниже.
                </p>
                <p className="mt-1.5">
                  <strong>Пусто</strong> — берётся справочный курс из /admin/rates (оценка по текущему
                  рынку). <strong>Заполнено</strong> — твой фактический фикс: курс, по которому реально
                  конвертируешь эту валюту. С фиксом профит — точный факт, а не оценка.
                </p>
                <p className="mt-1.5 text-text2">
                  Фикс можно уточнить и позже — в карточке заявки, до её закрытия. Изменения
                  попадают в аудит-журнал.
                </p>
              </HelpTip>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              {profitFixCcys.map((ccy) => {
                const isFixed = !!rateFix[ccy];
                return (
                  <div key={ccy} className="flex items-center gap-2 whitespace-nowrap text-[11px]">
                    <span className="font-mono text-xs font-semibold text-text">{ccy} → USDT</span>
                    <RateRefreshButton ccys={refreshAllCcys} />
                    <span className="text-xs text-text-subtle">1 USDT =</span>
                    <input
                      type="number"
                      step={RATE_UI_STEP}
                      value={rateFix[ccy] ?? ''}
                      placeholder={ratesMap[ccy] ?? '—'}
                      onChange={(e) =>
                        setRateFix((prev) => {
                          const next = { ...prev };
                          if (e.target.value === '') delete next[ccy];
                          else next[ccy] = e.target.value;
                          return next;
                        })
                      }
                      className={`${inputCls} ${noSpinCls} h-8 w-28 font-mono text-xs tabular-nums`}
                    />
                    <span className="font-mono text-xs text-text2">{ccy}</span>
                    <ProvBadge
                      meta={{ src: isFixed ? 'user' : 'auto', seq: 0 }}
                      onToggle={() =>
                        setRateFix((prev) => {
                          const next = { ...prev };
                          if (isFixed) delete next[ccy];
                          else if (ratesMap[ccy]) next[ccy] = ratesMap[ccy]!;
                          return next;
                        })
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {preview && (
          <div className="rounded-lg border border-border bg-bg3 p-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <SummaryCard
                tone="in"
                label="Получаем от клиента"
                hint={clientTotalHint && <FormulaHint formula={clientTotalHint} />}
                main={fmtAmt(clGiveWithCourier, v.clGiveCcy)}
                sub={(() => {
                  const total = preview.receiveUsdtFinal.plus(fixUsdtD).plus(courierExtraUsdtD).toFixed(2);
                  const notes: string[] = [];
                  // Порог ε: хвост округления receiveUsdtFinal−receiveUsdt давал «коэфф. 0.00».
                  if (preview.coefUsdt.abs().gte('0.005')) notes.push(`коэфф. ${preview.coefUsdt.toFixed(2)}`);
                  if (fixUsdtD.gt(0)) notes.push(`фикс ${fixUsdtD.toFixed(2)}`);
                  if (courierExtraUsdtD.gt(0)) notes.push(`курьер ${courierExtraUsdtD.toFixed(2)}`);
                  return `≈ ${total} USDT${notes.length ? `, из них ${notes.join(', ')}` : ''}`;
                })()}
              />
              <SummaryCard
                tone="out"
                label="Платим исполнителю"
                hint={execTotalHint && <FormulaHint formula={execTotalHint} />}
                main={fmtAmt(exGetWithCourier, v.exGetCcy)}
                sub={(() => {
                  const notes: string[] = [];
                  if (fixUsdtD.gt(0)) notes.push(`фикс ${fixUsdtD.toFixed(2)}`);
                  if (bonusUsdtD.gt(0)) notes.push(`бонус ${bonusUsdtD.toFixed(2)}`);
                  if (courierExtraUsdtD.gt(0)) notes.push(`курьер ${courierExtraUsdtD.toFixed(2)}`);
                  if (courier?.mode === 'included') notes.push(`курьер ${D(courier.usdt).toFixed(2)} удержан из выдачи`);
                  if (v.exGetCcy === 'USDT') return notes.length ? `вкл. ${notes.join(', ')}` : null;
                  const total = preview.payUsdt.plus(fixUsdtD).plus(bonusUsdtD).plus(courierExtraUsdtD).toFixed(2);
                  return `≈ ${total} USDT${notes.length ? `, вкл. ${notes.join(', ')}` : ''}`;
                })()}
              />
              <SummaryCard
                tone={preview.isNearZero ? 'neutral' : preview.isLoss ? 'out' : 'in'}
                label={
                  preview.isNearZero ? 'Безубыток' : preview.isLoss ? 'Убыток' : 'Профит'
                }
                hint={profitHint && <FormulaHint formula={profitHint} onCheckAction={onProfitCheckAction} />}
                main={
                  preview.isNearZero
                    ? '≈ 0 USDT'
                    : `${preview.profitUsdt.gte(0) ? '+' : ''}${preview.profitUsdt.toFixed(2)} USDT`
                }
                sub={
                  !preview.isNearZero &&
                  preview.nativeAmount &&
                  preview.nativeCcy &&
                  !D(preview.nativeAmount).isZero()
                    ? `≈ ${preview.nativeAmount.startsWith('-') ? '' : '+'}${fmtAmt(preview.nativeAmount, preview.nativeCcy)}`
                    : null
                }
                badge={
                  preview.isNearZero
                    ? '0%'
                    : `${preview.profitPct.gte(0) ? '+' : ''}${preview.profitPct.toFixed(2)}%`
                }
              />
            </div>

            {/* Поток денег через USDT — наглядный путь сделки */}
            <div className="mt-3 rounded-md border border-border-subtle bg-bg p-3">
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-text2">
                  Поток денег
                </span>
                <span className="text-[10px] text-text-subtle">все суммы сводятся к USDT</span>
              </div>
              <FlowSteps
                clGiveAmt={clGiveWithCourier}
                clGiveCcy={v.clGiveCcy}
                clGetAmt={clGetEffective}
                clGetCcy={v.clGetCcy}
                exGetAmt={exGetWithCourier}
                exGetCcy={v.exGetCcy}
                receiveUsdt={preview.receiveUsdtFinal.plus(fixUsdtD).plus(courierExtraUsdtD)}
                coefUsdt={preview.coefUsdt}
                payUsdt={preview.payUsdt.plus(fixUsdtD).plus(bonusUsdtD).plus(courierExtraUsdtD)}
                fixUsdt={fix?.usdt ?? null}
                bonusUsdt={bonus?.usdt ?? null}
                profitUsdt={preview.profitUsdt}
                giveRate={preview.giveRate}
                payRate={preview.payRate}
                isLoss={preview.isLoss}
                isNearZero={preview.isNearZero}
              />
            </div>

            {provodkiPreview && (
              <div className="mt-4">
                <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text2">Будут созданы проводки</h4>
                <div className="space-y-1.5">
                  {provodkiPreview.map((l, i) => {
                    // Sign-модель: amt<0 = «они нам должны» = приход → зелёный.
                    //              amt>0 = «мы им должны»   = расход → красный.
                    const isInflow = D(l.amt).isNegative();
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-3 rounded-md border px-3 py-2 text-xs ${
                          isInflow
                            ? 'border-[var(--green-border)] bg-[var(--green-bg)]'
                            : 'border-[var(--red-border)] bg-[var(--red-bg)]'
                        }`}
                      >
                        <span className="font-mono text-text2">{i + 1}.</span>
                        <span className="min-w-[180px] font-medium text-text">{l.label}</span>
                        <span className="font-mono font-semibold text-text2">{l.ccy}</span>
                        <span className={`font-mono font-bold tabular-nums ${isInflow ? 'text-success' : 'text-danger'}`}>
                          {fmtAmt(l.amt, l.ccy as CcyCode)}
                        </span>
                        <span className="ml-auto text-text2">→ {l.desc}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">
          <button type="button" onClick={close} className={btnSecondary}>
            Отмена
          </button>
          <button type="submit" disabled={create.isPending} className={btnPrimary}>
            {create.isPending ? 'Создание…' : 'Создать заявку'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({
  label,
  hint,
  reserveHintSpace,
  labelExtra,
  fhRef,
  children,
}: {
  label: React.ReactNode;
  hint?: string;
  /** Зарезервировать пустое место там, где hint отсутствует — чтобы парные Field
   *  в grid-row были одинаковой высоты и inputs были на одной линии. */
  reserveHintSpace?: boolean;
  /** Управляющие элементы справа от лейбла (бейдж происхождения, ⇄) —
   *  здесь, а не внутри инпута, чтобы не толкаться со значением. */
  labelExtra?: React.ReactNode;
  /** Идентификатор для FormulaHint (data-fh): подсветка поля из разбора. */
  fhRef?: string;
  children: React.ReactNode;
}) {
  const showHintSlot = !!hint || !!reserveHintSpace;
  // Шапка (лейбл + labelExtra) НЕ внутри <label>: label без htmlFor неявно
  // ассоциируется с ПЕРВЫМ labelable-потомком, а когда в labelExtra есть
  // кнопки (⇄ направления курса, ProvBadge) — им был именно первый <button>.
  // Клик по любому месту лейбла синтезировал клик по кнопке → у оператора
  // «сама» переворачивалась котировка. Теперь <label> оборачивает только
  // hint+input: клик по области — фокус в инпут, кнопки — только напрямую.
  return (
    <div className="mb-3 block" data-fh={fhRef}>
      <div className="mb-0.5 flex h-4 items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-text2">{label}</span>
        {labelExtra && <span className="flex shrink-0 items-center gap-1">{labelExtra}</span>}
      </div>
      <label className="block">
      {showHintSlot && (
        <div className="mb-1 text-[10px] leading-tight text-text-subtle">{hint || ' '}</div>
      )}
      {children}
      </label>
    </div>
  );
}

/**
 * Mini-сводка потока денег для одной стороны (клиент или исполнитель).
 * Показывает что идёт «нам» и что идёт «от нас» — критично для оператора,
 * чтобы он не путал направления.
 */
function FlowSummary({
  role,
  receive,
  send,
}: {
  role: 'client' | 'executor';
  receive: { amt: string; ccy: CcyCode };
  send: { amt: string; ccy: CcyCode };
}) {
  const fromLabel = role === 'client' ? 'клиента' : 'исполнителя';
  const toLabel = role === 'client' ? 'клиенту' : 'исполнителю';
  const recvAmt = D(receive.amt || '0');
  const sendAmt = D(send.amt || '0');

  return (
    <div className="mb-3 grid grid-cols-1 gap-1 rounded-md border border-border-subtle bg-bg3 px-3 py-2 text-[12px]">
      <div className="flex items-center gap-2">
        <ArrowDown />
        <span className="text-text2">Получаем от {fromLabel}:</span>
        <span className="ml-auto font-mono tabular-nums font-semibold text-success">
          {recvAmt.gt(0) ? fmtAmt(receive.amt, receive.ccy) : <span className="text-text-subtle">— {receive.ccy}</span>}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <ArrowUp />
        <span className="text-text2">Отправляем {toLabel}:</span>
        <span className="ml-auto font-mono tabular-nums font-semibold text-danger">
          {sendAmt.gt(0) ? fmtAmt(send.amt, send.ccy) : <span className="text-text-subtle">— {send.ccy}</span>}
        </span>
      </div>
    </div>
  );
}

/**
 * Компактная карточка-итог: лейбл + основная цифра + опциональный sub
 * (USDT-эквивалент или native) + опциональный badge (процент маржи).
 * Используется в превью «Получаем / Платим / Профит» — все три карточки
 * имеют одинаковый размер шрифта основной цифры (text-base).
 */
function SummaryCard({
  tone,
  label,
  main,
  sub,
  badge,
  hint,
}: {
  tone: 'in' | 'out' | 'neutral';
  label: string;
  main: string;
  sub: string | null;
  badge?: string;
  /** ƒ-иконка разбора арифметики (FormulaHint). */
  hint?: React.ReactNode;
}) {
  const palette = {
    in: { box: 'border-[var(--green-border)] bg-[var(--green-bg)]', text: 'text-success' },
    out: { box: 'border-[var(--red-border)] bg-[var(--red-bg)]', text: 'text-danger' },
    neutral: { box: 'border-[var(--accent-border)] bg-[var(--accent-bg)]', text: 'text-accent' },
  }[tone];
  return (
    <div className={`rounded-md border p-2.5 text-center ${palette.box}`}>
      <div className="flex items-center justify-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-text2">
        {label}
        {hint}
      </div>
      <div className={`mt-1 font-mono text-base font-bold tabular-nums ${palette.text}`}>
        {main}
      </div>
      <div className="mt-0.5 flex h-3.5 items-baseline justify-center gap-2 font-mono text-[11px] tabular-nums text-text2">
        {sub && <span>{sub}</span>}
        {badge && <span className={`font-medium ${palette.text}`}>{badge}</span>}
      </div>
    </div>
  );
}

/**
 * Блок-схема сделки: 3 узла (Клиент / Мы / Исполнитель), две «шины» между
 * ними. Верхняя стрелка ребра — прямой денежный поток (что мы получили / что
 * заплатили), нижняя — обратный поток валюты (что вернулось клиенту через
 * исполнителя). USDT-эквиваленты подписаны на верхних стрелках.
 */
function ArrowDown() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-success"
      aria-hidden="true"
    >
      <path d="M12 5v14M19 12l-7 7-7-7" />
    </svg>
  );
}

function ArrowUp() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-danger"
      aria-hidden="true"
    >
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

type RefreshIndicator = { kind: 'ok' | 'fail'; label: string; title: string };

/**
 * Принудительное обновление справочных USDT-курсов, из которых собран
 * рыночный курс пары. Спиннер — пока идёт обход цепочки источников.
 * После: «✓ ок» — API реально вернуло курс (гаснет само), «⚠ …» — какая
 * валюта не обновилась (остаётся, в title — причина).
 */
function RateRefreshButton({ ccys }: { ccys: CcyCode[] }) {
  const refresh = useRefreshRate();
  const [busy, setBusy] = useState(false);
  const [indicator, setIndicator] = useState<RefreshIndicator | null>(null);

  // Смена валютной пары — индикатор прошлого обновления больше не о том.
  const ccysKey = ccys.join(',');
  useEffect(() => setIndicator(null), [ccysKey]);

  // «ок» гаснет сам: через пару секунд это уже не «только что обновлено».
  useEffect(() => {
    if (indicator?.kind !== 'ok') return;
    const t = setTimeout(() => setIndicator(null), 4000);
    return () => clearTimeout(t);
  }, [indicator]);

  if (ccys.length === 0) return null;

  async function run() {
    if (busy) return;
    setBusy(true);
    setIndicator(null);
    try {
      const outcomes = await Promise.all(ccys.map((ccy) => refresh.mutateAsync(ccy)));
      const ok = outcomes.filter((o) => o.status === 'ok');
      const failed = outcomes.filter((o) => o.status !== 'ok');
      const okDetails = ok.map((o) => `${o.ccy}: ${o.rate} (${o.source ?? '?'})`).join(', ');
      if (failed.length === 0) {
        setIndicator({ kind: 'ok', label: '✓ ок', title: okDetails });
      } else if (ok.length === 0) {
        setIndicator({
          kind: 'fail',
          label: '⚠ не удалось',
          title: 'Ни один источник не вернул курс — проверь «⚙ Источники» на /admin/rates',
        });
      } else {
        setIndicator({
          kind: 'fail',
          label: `⚠ ${failed.map((o) => o.ccy).join(', ')} не обновился`,
          title: `Обновлено: ${okDetails}. Не удалось: ${failed.map((o) => o.ccy).join(', ')} — проверь «⚙ Источники» на /admin/rates`,
        });
      }
    } catch (e) {
      setIndicator({ kind: 'fail', label: '⚠ ошибка', title: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        tabIndex={-1}
        onClick={() => void run()}
        disabled={busy}
        title={`Обновить курс ${ccys.join(', ')} из настроенных источников`}
        aria-label="Обновить рыночный курс"
        className="inline-flex items-center rounded p-0.5 text-accent transition-colors duration-150 hover:bg-[var(--accent-bg)] disabled:cursor-wait"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={busy ? 'animate-spin' : ''}
          aria-hidden="true"
        >
          <path d="M21 12a9 9 0 1 1-2.64-6.36" />
          <path d="M21 3v6h-6" />
        </svg>
      </button>
      {indicator && (
        <span
          className={indicator.kind === 'ok' ? 'font-medium text-success' : 'text-warning'}
          title={indicator.title}
        >
          {indicator.label}
        </span>
      )}
    </span>
  );
}

function RateHint({
  marketRate,
  currentRate,
  intendedDeltaPct,
  dir,
  pair,
  refreshCcys,
  onApply,
  onDelta,
  onFlipFix,
  spreadHint,
  spreadComparison,
}: {
  marketRate: string | null;
  currentRate: string;
  /** «Намеренная» дельта (накопленная степпером/кастом-инпутом). Источник истины
   *  поверх вычисленной из округлённого курса — иначе precision 4 даёт артефакты
   *  (нажал +1% → видишь +0.9%). null = оператор сам редактировал курс. */
  intendedDeltaPct: string | null;
  dir: RateDir;
  pair: string;
  /** Валюты пары для принудительного обновления справочных курсов (без USDT). */
  refreshCcys?: CcyCode[];
  onApply: () => void;
  onDelta: (pct: number) => void;
  onFlipFix?: () => void;
  spreadHint?: boolean;
  spreadComparison?: string | null;
}) {
  if (!marketRate) return null;

  const cur = D(currentRate || '0');
  const mkt = D(marketRate);
  const matches = cur.equals(mkt);

  // Рынок и дельта показываются в ОТОБРАЖАЕМОМ направлении котировки —
  // чтобы числа в подсказке совпадали с тем, что оператор видит в инпуте.
  const dispMkt = dir === 'inv' ? D(1).dividedBy(mkt) : mkt;
  const dispCur = cur.gt(0) ? (dir === 'inv' ? D(1).dividedBy(cur) : cur) : null;
  const marketShown = dispMkt.toDecimalPlaces(RATE_UI_SCALE).toString();

  const computedPct = dispCur && !dispMkt.isZero()
    ? dispCur.minus(dispMkt).dividedBy(dispMkt).times(100).toDecimalPlaces(1).toString()
    : null;
  const deltaPct =
    intendedDeltaPct !== null
      ? D(intendedDeltaPct).toDecimalPlaces(2).toString()
      : computedPct;

  // Детектор перепутанного направления: введённый курс дико отличается от
  // рынка (>25%), а его ОБРАТНАЯ величина — близка (<8%). Классика: оператор
  // ввёл котировку из сообщения исполнителя, не глядя на направление.
  const wrongDirectionLikely =
    !!onFlipFix &&
    cur.gt(0) &&
    !matches &&
    cur.minus(mkt).abs().dividedBy(mkt).gt('0.25') &&
    D(1).dividedBy(cur).minus(mkt).abs().dividedBy(mkt).lt('0.08');

  return (
    <div className="mt-1.5 space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
        <span className="text-text2">
          Рынок {pair}:{' '}
          <span className="font-mono font-medium text-text">{marketShown}</span>
        </span>
        {refreshCcys && <RateRefreshButton ccys={refreshCcys} />}
        {!matches && deltaPct && (
          <span
            className={`font-mono font-medium ${
              D(deltaPct).gte(0) ? 'text-success' : 'text-danger'
            }`}
          >
            {D(deltaPct).gte(0) ? '+' : ''}
            {deltaPct}%
          </span>
        )}
        {!matches && (
          <button
            type="button"
            onClick={onApply}
            className="rounded px-1.5 py-0.5 font-medium text-accent transition-colors duration-150 hover:bg-[var(--accent-bg)]"
          >
            ↺ к рынку
          </button>
        )}
        {wrongDirectionLikely && (
          <button
            type="button"
            onClick={onFlipFix}
            title="Число останется как введено, но будет понято в обратной котировке"
            className="rounded-md border border-[var(--orange-border,#fdba74)] bg-[var(--orange-bg,#fff7ed)] px-1.5 py-0.5 font-medium text-warning transition-colors duration-150 hover:opacity-80"
          >
            ⚠ похоже, направление перепутано — исправить ⇄
          </button>
        )}
        {spreadHint && spreadComparison && !D(spreadComparison).isZero() && (
          <span className="text-text2">
            · спред{' '}
            <span
              className={`font-mono font-medium ${
                D(spreadComparison).gt(0) ? 'text-success' : 'text-danger'
              }`}
            >
              {D(spreadComparison).gt(0) ? '+' : ''}
              {spreadComparison}
            </span>
          </span>
        )}
      </div>
      <RateStepper currentRate={currentRate} onDelta={onDelta} />
    </div>
  );
}

const STEP_PRESETS = [-1, -0.5, -0.1, 0.1, 0.5, 1];

function RateStepper({
  currentRate,
  onDelta,
}: {
  currentRate: string;
  onDelta: (pct: number) => void;
}) {
  const [customPct, setCustomPct] = useState('');
  const disabled = !currentRate || D(currentRate || '0').lte(0);

  function applyCustom(sign: 1 | -1) {
    const pct = parseFloat(customPct);
    if (!Number.isFinite(pct) || pct <= 0) return;
    onDelta(sign * pct);
    setCustomPct('');
  }

  function onCustomKey(e: React.KeyboardEvent) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const pct = parseFloat(customPct);
    if (!Number.isFinite(pct) || pct === 0) return;
    onDelta(pct);
    setCustomPct('');
  }

  return (
    <div className="inline-flex h-7 max-w-full items-stretch overflow-hidden rounded-md border border-border bg-bg">
      {STEP_PRESETS.map((pct, i) => {
        const isPos = pct > 0;
        return (
          <button
            key={pct}
            type="button"
            disabled={disabled}
            onClick={() => onDelta(pct)}
            aria-label={`${isPos ? 'Прибавить' : 'Отнять'} ${Math.abs(pct)} процент`}
            className={`inline-flex items-center px-2 font-mono text-[11px] font-semibold tabular-nums transition-colors duration-150 ${
              i === 0 ? '' : 'border-l border-border'
            } ${
              isPos
                ? 'text-success hover:bg-[var(--green-bg)]'
                : 'text-danger hover:bg-[var(--red-bg)]'
            } disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent`}
          >
            {isPos ? '+' : ''}
            {pct}%
          </button>
        );
      })}
      {/* разделитель */}
      <div className="w-px bg-border-strong" aria-hidden="true" />
      {/* custom: один input, signed (-1.5 = −1.5%, 1.5 = +1.5%), Enter применяет */}
      <input
        type="number"
        step="0.01"
        inputMode="decimal"
        value={customPct}
        onChange={(e) => setCustomPct(e.target.value)}
        onKeyDown={onCustomKey}
        disabled={disabled}
        placeholder="±%"
        aria-label="Свой процент (Enter применить)"
        className="block w-14 bg-bg px-1 text-center font-mono text-[11px] tabular-nums text-text outline-none focus:bg-bg2 disabled:cursor-not-allowed disabled:opacity-40"
      />
    </div>
  );
}
